<template>
    <div :class="classList">
        <div ref="window" class="app-scrollbar__window" @scroll="handleScroll">
            <div ref="content" class="app-scrollbar__content">
                <slot />
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'AppScrollbar',
    props: {
        watch: {
            type: [Boolean, Number],
            default: true
        },
        noOverflowBorders: {
            type: Boolean,
            default: false
        },
        alwaysScrollY: {
            type: Boolean,
            default: false
        },
        alwaysScrollX: {
            type: Boolean,
            default: false
        },
        noPadding: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        $window: null,
        $content: null,
        handleScroll: () => {},
        lastScrollTop: 0,
        lastScrollLeft: 0,
        resizeObserver: null,
        watchTimer: null,
        scrollY: false,
        scrollX: false,
        scrollUp: false,
        scrollDown: false,
        scrollLeft: false,
        scrollRight: false,
        yReachStart: false,
        yReachEnd: false,
        xReachStart: false,
        xReachEnd: false
    }),
    computed: {
        scrollTopMax() {
            return this.$window.scrollHeight - this.$window.offsetHeight;
        },
        scrollLeftMax() {
            return this.$window.scrollWidth - this.$window.offsetWidth;
        },
        classList() {
            let classList = ['app-scrollbar'];

            if (this.noOverflowBorders) {
                classList.push('app-scrollbar--no-overflow-borders');
            }

            if (this.noPadding) {
                classList.push('app-scrollbar--no-padding');
            }

            if (this.scrollY) {
                classList.push('app-scrollbar--scroll-y');
            }

            if (this.scrollX) {
                classList.push('app-scrollbar--scroll-x');
            }

            if (this.scrollUp) {
                classList.push('app-scrollbar--scroll-up');
            }

            if (this.scrollDown) {
                classList.push('app-scrollbar--scroll-down');
            }

            if (this.scrollLeft) {
                classList.push('app-scrollbar--scroll-left');
            }

            if (this.scrollRight) {
                classList.push('app-scrollbar--scroll-right');
            }

            if (this.yReachStart) {
                classList.push('app-scrollbar--y-reach-start');
            }

            if (this.yReachEnd) {
                classList.push('app-scrollbar--y-reach-end');
            }

            if (this.xReachStart) {
                classList.push('app-scrollbar--x-reach-start');
            }

            if (this.xReachEnd) {
                classList.push('app-scrollbar--x-reach-end');
            }

            if (this.alwaysScrollY) {
                classList.push('app-scrollbar--always-scroll-y');
            }

            if (this.alwaysScrollX) {
                classList.push('app-scrollbar--always-scroll-x');
            }

            return classList.join(' ');
        }
    },
    async mounted() {
        await this.init();
    },
    beforeDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },
    methods: {
        async init() {
            this.handleScroll = this._.debounce(this.onScroll, 25);
            await this.$nextTick();
            await this.$nextTick();

            this.$window = this.$refs.window;
            this.$content = this.$refs.content;

            if (this.$window) {
                this.lastScrollTop = this.$window.scrollTop;
                this.lastScrollLeft = this.$window.scrollLeft;

                if (this.watch) {
                    const interval = this._.isNumber(this.watch) ? this.watch : 300;
                    this.watchTimer = setInterval(() => this.handleScroll(), interval);
                } else {
                    this.resizeObserver = new ResizeObserver(this.handleScroll);
                    this.resizeObserver.observe(this.$content);
                }
            }

            this.reset();
        },
        reset() {
            // this.scrollY = false;
            // this.scrollX = false;
            this.scrollUp = false;
            this.scrollDown = false;
            this.scrollLeft = false;
            this.scrollRight = false;
            this.yReachStart = false;
            this.yReachEnd = false;
            this.xReachStart = false;
            this.xReachEnd = false;

            if (this.$window) {
                if (this.$window.scrollHeight <= this.$window.offsetHeight) {
                    this.$window.style.overflowY = 'hidden';
                } else {
                    this.$window.style.overflowY = 'scroll';
                }

                if (this.$window.scrollWidth <= this.$window.offsetWidth) {
                    this.$window.style.overflowX = 'hidden';
                } else {
                    this.$window.style.overflowX = 'scroll';
                }
            }
        },
        onScroll() {
            this.reset();

            //

            if (!this.$window) {
                return;
            }

            const scrollTop = this.$window.scrollTop;

            if (scrollTop !== this.lastScrollTop) {
                this.onScrollY();
            }

            if (scrollTop < this.lastScrollTop) {
                this.onScrollUp();
            }

            if (scrollTop > this.lastScrollTop) {
                this.onScrollDown();
            }

            if (scrollTop === 0) {
                this.onYReachStart();
            }

            if (scrollTop === this.scrollTopMax) {
                this.onYReachEnd();
            }

            if (scrollTop <= 0) {
                this.lastScrollTop = 0;
            } else if (scrollTop >= this.scrollTopMax) {
                this.lastScrollTop = this.scrollTopMax;
            } else {
                this.lastScrollTop = scrollTop;
            }

            //

            const scrollLeft = this.$window.scrollLeft;

            if (scrollLeft !== this.lastScrollLeft) {
                this.onScrollX();
            }

            if (scrollLeft < this.lastScrollLeft) {
                this.onScrollLeft();
            }

            if (scrollLeft > this.lastScrollLeft) {
                this.onScrollRight();
            }

            if (scrollLeft === 0) {
                this.onXReachStart();
            }

            if (scrollLeft === this.scrollLeftMax) {
                this.onXReachEnd();
            }

            if (scrollLeft <= 0) {
                this.lastScrollLeft = 0;
            } else if (scrollLeft >= this.scrollLeftMax) {
                this.lastScrollLeft = this.scrollLeftMax;
            } else {
                this.lastScrollLeft = scrollLeft;
            }
        },
        onScrollY() {
            this.scrollY = true;
        },
        onScrollUp() {
            this.scrollUp = true;
            this.scrollDown = false;
            this.yReachEnd = false;
        },
        onScrollDown() {
            this.scrollDown = true;
            this.scrollUp = false;
            this.yReachStart = false;
        },
        onYReachStart() {
            this.yReachStart = true;
        },
        onYReachEnd() {
            this.yReachEnd = true;
        },
        onScrollX() {
            this.scrollX = true;
        },
        onScrollLeft() {
            this.scrollLeft = true;
            this.scrollRight = false;
            this.xReachEnd = false;
        },
        onScrollRight() {
            this.scrollRight = true;
            this.scrollLeft = false;
            this.xReachStart = false;
        },
        onXReachStart() {
            this.xReachStart = true;
        },
        onXReachEnd() {
            this.xReachEnd = true;
        }
    }
};
</script>

<style lang="scss" scoped>
.app-scrollbar {
    position: relative;
    &__window {
        position: relative;
        overflow: hidden;
        height: 100%;
        width: 100%;
        padding: 1rem 1rem 1rem 0;
    }
    &__content {
        height: 100%;
        width: 100%;
    }

    &--no-padding {
        > .app-scrollbar__window {
            padding: 0;
        }
    }

    &--scroll-y {
        &:before {
            content: '';
            z-index: 1;
            position: absolute;
            top: 0;
            left: 0;
            height: 2px;
            width: 100%;
            background: var(--v-background-darken3);
        }
        &:after {
            content: '';
            z-index: 1;
            position: absolute;
            bottom: 0;
            left: 0;
            height: 2px;
            width: 100%;
            background: var(--v-background-darken3);
        }
    }
    &--y-reach-start:before {
        content: none;
    }
    &--y-reach-end:after {
        content: none;
    }
    &--no-overflow-borders {
        &:before,
        &:after {
            content: none;
        }
    }
    &.app-scrollbar--y-reach-start.app-scrollbar--x-reach-end {
        &:before,
        &:after {
            content: none;
        }
    }

    &--always-scroll-y {
        > .app-scrollbar__window {
            overflow-y: scroll !important;
        }
    }

    &--always-scroll-x {
        > .app-scrollbar__window {
            overflow-x: scroll !important;
        }
    }
}
</style>
